/**
 * VivaDecor – cart integrity for discounted add-on decor.
 *
 * Add-on lines (line property _vd_addon = "1" or product type "Add-on") may only exist together with
 * their background line (a non add-on line with the same _vd_bundle property). Whenever the cart changes
 * (theme event "cart:update" – cart drawer / cart page remove or quantity change, product form add) and
 * on page load when Liquid detected orphaned add-on lines, orphaned add-on lines are removed.
 *
 * Loop-safe: this module never dispatches "cart:update" itself; it refreshes the cart sections quietly
 * (morphSection) and the cart bubble, and runs at most one check at a time (bounded retries).
 */
import { morphSection } from '@theme/section-renderer';

const VD_ADDON_TYPE = 'Add-on';

/**
 * @typedef {{key: string, product_type?: string, properties?: Record<string, any> | null}} VdCartItem
 * @typedef {{items: VdCartItem[], item_count?: number, sections?: Record<string, string>}} VdCart
 */

/**
 * @param {VdCartItem} item
 * @returns {boolean}
 */
function vdIsAddon(item) {
  const properties = item.properties || {};
  return String(properties._vd_addon || '') === '1' || item.product_type === VD_ADDON_TYPE;
}

/**
 * @param {VdCart | null | undefined} cart
 * @returns {VdCartItem[]} The add-on lines without a matching background line
 */
function vdFindOrphans(cart) {
  if (!cart || !Array.isArray(cart.items)) return [];

  const bundles = new Set();
  for (const item of cart.items) {
    if (vdIsAddon(item)) continue;
    const bundle = item.properties?._vd_bundle;
    if (bundle) bundles.add(String(bundle));
  }

  return cart.items.filter((item) => {
    if (!vdIsAddon(item)) return false;
    const bundle = item.properties?._vd_bundle;
    return !bundle || !bundles.has(String(bundle));
  });
}

/** @returns {string} */
function vdRoute(name, fallback) {
  // @ts-ignore Theme is a global defined in snippets/scripts.liquid
  const routes = typeof Theme !== 'undefined' && Theme.routes ? Theme.routes : {};
  return routes[name] || fallback;
}

/** @returns {Promise<VdCart | null>} */
async function vdFetchCart() {
  const cartUrl = vdRoute('cart_url', '/cart');
  const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) return null;
  return response.json();
}

/**
 * Removes the given lines and quietly refreshes the cart UI.
 * @param {VdCartItem[]} lines
 * @returns {Promise<VdCart | null>} The updated cart
 */
async function vdRemoveLines(lines) {
  /** @type {Record<string, number>} */
  const updates = {};
  for (const line of lines) {
    if (line.key) updates[line.key] = 0;
  }
  if (Object.keys(updates).length === 0) return null;

  const cartItemsComponents = Array.from(document.querySelectorAll('cart-items-component'));
  const sectionIds = [
    ...new Set(
      cartItemsComponents
        .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
        .filter(Boolean)
    ),
  ];

  /** @type {Record<string, any>} */
  const body = { updates };
  if (sectionIds.length > 0) {
    body.sections = sectionIds.join(',');
    body.sections_url = window.location.pathname;
  }

  const updateUrl = vdRoute('cart_update_url', '/cart/update');
  const response = await fetch(`${updateUrl}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) return null;

  /** @type {VdCart} */
  const cart = await response.json();
  if (!cart || !Array.isArray(cart.items)) return null;

  // Refresh cart drawer / cart page sections
  for (const sectionId of sectionIds) {
    const html = cart.sections?.[sectionId];
    if (!html) continue;
    try {
      await morphSection(sectionId, html);
    } catch (error) {
      console.error(error);
    }
  }

  // Refresh the header cart bubble without animation
  if (typeof cart.item_count === 'number') {
    for (const icon of document.querySelectorAll('cart-icon')) {
      const renderCartBubble = /** @type {any} */ (icon).renderCartBubble;
      if (typeof renderCartBubble === 'function') renderCartBubble(cart.item_count, false, false);
    }
  }

  return cart;
}

/** @type {Promise<void> | null} */
let vdRunning = null;
let vdCheckAgain = false;

/**
 * Checks the cart for orphaned add-on lines and removes them.
 * @param {VdCart | null | undefined} knownCart - A cart already returned by the theme (avoids a fetch)
 */
async function vdCheckCart(knownCart) {
  if (vdRunning) {
    vdCheckAgain = true;
    return;
  }

  vdRunning = (async () => {
    try {
      let cart = knownCart && Array.isArray(knownCart.items) ? knownCart : await vdFetchCart();

      // At most 2 removal rounds per check – never loops.
      for (let round = 0; round < 2 && cart; round++) {
        const orphans = vdFindOrphans(cart);
        if (orphans.length === 0) break;
        cart = await vdRemoveLines(orphans);
      }
    } catch (error) {
      console.error(error);
    }
  })();

  await vdRunning;
  vdRunning = null;

  if (vdCheckAgain) {
    vdCheckAgain = false;
    await vdCheckCart(null);
  }
}

// @ts-ignore
if (!window.__vdAddonsCartGuard) {
  // @ts-ignore
  window.__vdAddonsCartGuard = true;

  document.addEventListener('cart:update', (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (detail?.data?.didError) return;
    const resource = detail?.resource;
    vdCheckCart(resource && Array.isArray(resource.items) ? resource : null);
  });

  // Orphaned add-on lines detected by Liquid when the page was rendered (snippets/vd-addons-cart.liquid)
  if (document.querySelector('[data-vd-addons-orphans]')) {
    vdCheckCart(null);
  }
}
